import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { Box, Typography, IconButton, Card, CardContent, CircularProgress, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CustomButton } from "../Button/Button";
import { useProducts, useRenewSubscription } from "../../utils/reactQuery/subscription";
import ErrorIcon from '@mui/icons-material/Error';
import "./subscriptionPopup.scss";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const SubscriptionExpiredPopup = ({ open, onClose, onSelectPlan, disableClose = false }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { data: productsData, isLoading: isLoadingProducts, error: productsError } = useProducts();
  const { mutate: renewSubscription, isPending: isRenewing } = useRenewSubscription();

  // Transform API response to plans format
  const plans = useMemo(() => {
    if (!productsData?.data?.data?.responseData?.responseData) {
      return [];
    }

    const products = productsData.data.data.responseData.responseData;
    const transformedPlans = [];

    // Process each product and its prices
    products.forEach((product) => {
      if (!product.active || !product.prices || product.prices.length === 0) {
        return;
      }

      // Filter only recurring prices
      const recurringPrices = product.prices.filter(
        (price) => price.type === "recurring" && price.recurring
      );

      recurringPrices.forEach((price) => {
        const priceInDollars = price.unit_amount / 100; // Convert cents to dollars
        const interval = price.recurring.interval; // 'month' or 'year'
        
        // Generate plan name from nickname or product name + interval
        const planName = price.nickname || `${product.name} - ${interval.charAt(0).toUpperCase() + interval.slice(1)}`;
        
        // Generate unique ID from price ID
        const planId = price.id;

        transformedPlans.push({
          id: planId,
          priceId: price.id,
          productId: product.id,
          name: planName,
          price: priceInDollars,
          period: interval,
          description: price.nickname || `Billed ${interval}ly`,
          savings: null,
          originalPrice: price,
        });
      });
    });

    // Calculate savings for plans with same product but different intervals
    // Mark the first plan as popular if no other logic is needed
    if (transformedPlans.length > 0) {
      transformedPlans[0].popular = true;
    }

    // Calculate savings by comparing monthly vs yearly for same product
    transformedPlans.forEach((plan) => {
      if (plan.period === "year") {
        const monthlyPlan = transformedPlans.find(
          (p) => p.productId === plan.productId && p.period === "month"
        );
        if (monthlyPlan) {
          const yearlyFromMonthly = monthlyPlan.price * 12;
          const savings = yearlyFromMonthly - plan.price;
          if (savings > 0) {
            plan.savings = `Save $${savings.toFixed(2)} per year`;
            // Mark yearly plan as popular if it has savings
            plan.popular = true;
            monthlyPlan.popular = false;
          }
        }
      }
    });

    // Sort plans: yearly first (if has savings), then monthly
    transformedPlans.sort((a, b) => {
      if (a.period === "year" && b.period === "month") return -1;
      if (a.period === "month" && b.period === "year") return 1;
      return 0;
    });

    return transformedPlans;
  }, [productsData]);

  // Reset selected plan when popup closes
  useEffect(() => {
    if (!open) {
      setSelectedPlan(null);
    }
  }, [open]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan.id);
  };

  const handleSubscribe = () => {
    if (!selectedPlan || isRenewing) return;

    const plan = plans.find((p) => p.id === selectedPlan);
    if (!plan) {
      return;
    }

    // Get current website URL for redirect (including pathname and search params)
    const redirectUrl = window.location.origin + window.location.pathname + window.location.search;

    // Call the renew subscription API
    renewSubscription(
      {
        price_id: plan.priceId,
        redirect_url: redirectUrl,
      },
      {
        onSuccess: (response) => {
          // Check for checkout_url in the response (Stripe checkout URL)
          const checkoutUrl = 
            response?.data?.checkout_url || 
            response?.data?.data?.checkout_url ||
            response?.checkout_url;

          if (checkoutUrl) {
            // Redirect to Stripe checkout page
            window.location.href = checkoutUrl;
          } else {
            // Fallback: If no checkout URL in response, call callback
            // Call the onSelectPlan callback if provided
            if (onSelectPlan && typeof onSelectPlan === "function") {
              onSelectPlan(plan);
            }
          }
        },
        onError: (error) => {
          // Call the onSelectPlan callback if provided (for backward compatibility)
          if (onSelectPlan && typeof onSelectPlan === "function") {
            onSelectPlan(plan);
          }
        },
      }
    );
  };

  const formatPrice = (price) => {
    // Format price to 2 decimal places if needed
    const isWholeNumber = price % 1 === 0;
    return `$${isWholeNumber ? price.toFixed(0) : price.toFixed(2)}`;
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={disableClose ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={disableClose}
      className="subscription-expired-dialog"
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "8px",
          maxWidth: "600px",
        },
      }}
    >
      {/* <DialogTitle className="subscription-dialog-title">
        <Box className="subscription-title-content">
          <Typography variant="h5" className="subscription-title-text">
            Subscription Expired
          </Typography>
          {!disableClose && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              className="subscription-close-icon"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle> */}

      <DialogContent className="subscription-dialog-content">
        <Box className="subscription-content-wrapper">
          <ErrorIcon />
          <Typography variant="h3" className="subscription-heading">
            Subscription expired!
          </Typography>
          <Typography variant="body1" className="subscription-message">
            Your subscription has expired. Please select a plan to continue using
            our services.
          </Typography>

          {isLoadingProducts && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {productsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load subscription plans. Please try again later.
            </Alert>
          )}

          {!isLoadingProducts && !productsError && plans.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No subscription plans available at the moment.
            </Alert>
          )}

          <Box className="plans-container">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`plan-card ${selectedPlan === plan.id ? "selected" : ""} ${
                  plan.popular ? "popular" : ""
                }`}
                onClick={() => handlePlanSelect(plan)}
              >
                {plan.popular && (
                  <Box className="popular-badge">Most Popular</Box>
                )}
                <CardContent className="plan-card-content">
                  <Box className="plan-header">
                    <Typography variant="h6" className="plan-name">
                      {plan.name}
                    </Typography>
                    {selectedPlan === plan.id && (
                      <CheckCircleIcon className="selected-icon" />
                    )}
                  </Box>

                  <Box className="plan-pricing">
                    <Typography variant="h4" className="plan-price">
                      {formatPrice(plan.price)}
                    </Typography>
                    <Typography variant="body2" className="plan-period">
                      /{plan.period}
                    </Typography>
                  </Box>

                  {plan.savings && (
                    <Typography variant="body2" className="plan-savings">
                      {plan.savings}
                    </Typography>
                  )}

                  <Typography variant="body2" className="plan-description">
                    {plan.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box className="subscription-actions">
            <CustomButton
              variant="contained"
              onClick={handleSubscribe}
              disabled={!selectedPlan || isRenewing}
              loading={isRenewing}
              fullWidth
              sx={{
                bgcolor: "#1b3e61",
                fontSize: "16px",
                padding: "12px 24px",
                fontWeight: "600",
                textTransform: "capitalize",
                mt: 2,
              }}
            >
              {isRenewing ? "Processing..." : "Subscribe Now"}
            </CustomButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(SubscriptionExpiredPopup);

